import type { CityConfig } from "../city-template/types";

export const minneapolisConfig: CityConfig = {
  city: "Minneapolis",
  state: "Minnesota",
  stateAbbr: "MN",
  slug: "minneapolis",
  route: "/cities/minneapolis",
  title: "Emergency & Fast Passport Renewal Minneapolis MN | Same-Day & Expedited | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Minneapolis, MN — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Hennepin County and Twin Cities travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Minneapolis, MN",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Minneapolis and Twin Cities travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Minneapolis%20skyline%20at%20golden%20hour%20with%20modern%20downtown%20skyscrapers%2C%20Stone%20Arch%20Bridge%20over%20the%20Mississippi%20River%2C%20warm%20amber%20sunset%20light%20reflecting%20on%20glass%20buildings%2C%20clean%20modern%20urban%20architecture%20with%20parks%20and%20green%20spaces%2C%20professional%20cityscape%20photography%20with%20warm%20earth%20tones%2C%20no%20readable%20text%20or%20people%2C%20crisp%20and%20inviting%20atmosphere&width=1800&height=700&seq=minneapolis-hero-001&orientation=landscape",
  heroImageAlt: "Minneapolis skyline with Stone Arch Bridge and Mississippi River at golden hour",
  geoPosition: "44.9778;-93.2650",
  geoPlacename: "Minneapolis, Minnesota",
  lastModified: "2026-06-02",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "101 W Congress Pkwy, Chicago, IL 60605",
    distance: "Chicago is 6–7 hours by car from Minneapolis — our courier eliminates the need to drive",
  },
  nearbyCitiesZips: [
    { name: "Downtown Minneapolis", zips: "55401–55405" },
    { name: "Uptown", zips: "55408, 55409" },
    { name: "Northeast Minneapolis", zips: "55413, 55414" },
    { name: "St. Louis Park", zips: "55416, 55426" },
    { name: "Edina", zips: "55410, 55424, 55435" },
    { name: "Richfield", zips: "55423" },
    { name: "Bloomington", zips: "55420, 55425, 55431" },
    { name: "Brooklyn Park", zips: "55428, 55429, 55443" },
    { name: "Plymouth", zips: "55441, 55442, 55446" },
    { name: "Maple Grove", zips: "55311, 55369" },
    { name: "Minnetonka", zips: "55305, 55343, 55345" },
    { name: "Eden Prairie", zips: "55344, 55346, 55347" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Twin Cities departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in St. Paul", route: "/cities/st-paul" },
    { name: "Emergency passport help in Duluth", route: "/cities/duluth-mn" },
    { name: "Urgent passport services in Rochester", route: "/cities/rochester-mn" },
    { name: "Expedited passport services in Chicago", route: "/states/illinois" },
    { name: "Passport courier services in Iowa", route: "/states/iowa" },
    { name: "Rush passport help in Milwaukee", route: "/states/illinois" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Minneapolis remotely via secure courier. We do not have a walk-in office in Minneapolis — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Minneapolis travelers.",
  fpcOfficeDistance: "Serving all of Hennepin County and the Twin Cities metro",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout the entire Minneapolis metro area. From downtown Minneapolis to the western suburbs, from the University of Minnesota to the Mall of America area — we help Twin Cities travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Minneapolis neighborhoods and nearby suburbs including St. Paul, Bloomington, Edina, St. Louis Park, Plymouth, Maple Grove, and Brooklyn Park. If you are in the Twin Cities metro, we have you covered.",
  howItWorksIntro: "Most Minneapolis travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line. Our emergency and expedited passport process is designed for Twin Cities travelers who need fast, reliable results.",
  mistakesIntro: "With Minneapolis–Saint Paul International Airport (MSP) being a major international hub, Twin Cities travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Minneapolis travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Minneapolis? Here are the most common ones we hear from Twin Cities travelers.",
  statePageRoute: "/states/minnesota",
  statePageName: "Minnesota",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Twin Cities international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Sarah M.",
      location: "Uptown Minneapolis, MN",
      rating: 5,
      text: "My flight to London was in 4 days and I realized my passport was expired. Fast Passport Center got me a new one in 48 hours. Absolutely saved my business trip.",
      service: "Emergency Passport",
    },
    {
      name: "James K.",
      location: "Edina, MN",
      rating: 5,
      text: "I needed to renew my passport for a family vacation to Japan. The process was smooth, they reviewed my documents, and I had my passport in 5 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Emily R.",
      location: "Bloomington, MN",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Italy. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Michael T.",
      location: "Plymouth, MN",
      rating: 5,
      text: "As a frequent international traveler for Target corporate, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional, fast, and reliable every time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "mpls-1",
      name: "Minneapolis Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 S 1st St",
      city: "Minneapolis",
      state: "MN",
      zip: "55401",
      phone: "(612) 333-2306",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Minneapolis",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "mpls-2",
      name: "University of Minnesota Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "330 21st Ave S",
      city: "Minneapolis",
      state: "MN",
      zip: "55455",
      phone: "(612) 625-5000",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Near the University of Minnesota East Bank campus",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for students and faculty. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "mpls-3",
      name: "St. Louis Park Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4550 Excelsior Blvd",
      city: "St. Louis Park",
      state: "MN",
      zip: "55416",
      phone: "(952) 920-6511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4.5 miles from downtown Minneapolis",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for west metro residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "mpls-4",
      name: "Bloomington Civic Center",
      type: "City Clerk Passport Acceptance",
      address: "1800 W Old Shakopee Rd",
      city: "Bloomington",
      state: "MN",
      zip: "55431",
      phone: "(952) 563-8700",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "10 miles from downtown Minneapolis",
      website: "https://www.bloomingtonmn.gov/",
      websiteLabel: "Bloomington City Clerk",
      notes: "Accepts walk-ins but appointments preferred. Near the Mall of America.",
      icon: "ri-government-line",
    },
    {
      id: "mpls-5",
      name: "Brooklyn Park Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "5000 Xerxes Ave N",
      city: "Brooklyn Park",
      state: "MN",
      zip: "55443",
      phone: "(763) 560-5240",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12 miles from downtown Minneapolis",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for north metro residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "mpls-6",
      name: "Hennepin County Service Center",
      type: "County Clerk Passport Acceptance",
      address: "300 E 6th St",
      city: "Minneapolis",
      state: "MN",
      zip: "55415",
      phone: "(612) 348-8240",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.5 miles from downtown Minneapolis",
      website: "https://www.hennepin.us/",
      websiteLabel: "Hennepin County",
      notes: "Major county facility. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 6+ hours to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Twin Cities photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Minneapolis acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Minneapolis applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Minneapolis families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Minneapolis travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Minneapolis." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Minneapolis address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Minneapolis?",
      a: "Fast Passport Center offers emergency passport processing in Minneapolis in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Twin Cities travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Minneapolis?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel within 72 hours. The nearest agency to Minneapolis is in Chicago — a 6–7 hour drive. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Minneapolis residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Minneapolis?",
      a: "Fast Passport Center offers fast passport renewal in Minneapolis with no office visit required. We serve all Twin Cities neighborhoods including Uptown, Northeast, St. Louis Park, Edina, Bloomington, and beyond. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Minneapolis?",
      a: "The fastest way to get an urgent passport in Minneapolis is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (2–3 weeks).",
    },
    {
      q: "How fast can I get a passport in Minneapolis?",
      a: "With Fast Passport Center's emergency courier service, Minneapolis travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit a passport agency in person?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Minneapolis?",
      a: "Popular options include the Minneapolis Main Post Office (55401), University of Minnesota Post Office (55455), St. Louis Park Post Office (55416), and Hennepin County Service Center (55415). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in the western suburbs like Plymouth or Maple Grove?",
      a: "We serve all of the Twin Cities metro including Plymouth, Maple Grove, Brooklyn Park, Minnetonka, and Eden Prairie. Our overnight shipping labels work from any UPS location, so your location within the metro doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Minneapolis?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Minneapolis?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Minneapolis offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Minneapolis?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Minneapolis?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Minneapolis families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Uptown", address: "2428 Hennepin Ave, Minneapolis, MN 55405", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Downtown", address: "1200 Nicollet Mall, Minneapolis, MN 55403", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Minneapolis", address: "80 S 8th St, Minneapolis, MN 55402", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — St. Louis Park", address: "5120 W 66th St, Minneapolis, MN 55424", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Bloomington", address: "700 American Blvd E, Bloomington, MN 55420", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Edina", address: "5125 Eden Ave, Edina, MN 55436", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Brooklyn Park", address: "7600 Zane Ave N, Brooklyn Park, MN 55443", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Plymouth", address: "3600 Plymouth Blvd, Plymouth, MN 55446", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Downtown Minneapolis",
      address: "300 S 6th St, Minneapolis, MN 55415",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — St. Louis Park",
      address: "5400 Auto Club Way, Minneapolis, MN 55416",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Bloomington",
      address: "9800 Lyndale Ave S, Bloomington, MN 55420",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Brooklyn Park",
      address: "8500 Zane Ave N, Brooklyn Park, MN 55443",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center. Extended hours available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "MSP",
      name: "Minneapolis–Saint Paul International Airport",
      distance: "10 miles from downtown Minneapolis",
      address: "4300 Glumack Dr, St. Paul, MN 55111",
      note: "The primary international airport for the Twin Cities. Major hub for international flights to Europe, Asia, and the Americas.",
      flightUrl: "https://www.mspairport.com/",
      flightLabel: "MSP Flight Info",
      featured: true,
    },
    {
      code: "FCM",
      name: "Flying Cloud Airport",
      distance: "18 miles from downtown Minneapolis",
      address: "10110 Flying Cloud Dr, Eden Prairie, MN 55347",
      note: "General aviation airport. No commercial international flights.",
      flightUrl: "https://www.metroairports.org/",
      flightLabel: "Flying Cloud Airport",
      featured: false,
    },
  ],
};